import { useTranslation } from "react-i18next";
import { Form } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { CUSTOMER_TYPES } from "models";
import { CUSTOMER_DRIVER_FIELDS, CUSTOMER_OWNER_CONTRACTOR_FIELDS } from "modules/Appointments/components/NewAppointment";
import { hasAnyFieldValue } from "modules/Appointments/components/NewAppointment/utils";
import { isKeyLoopLocation } from "utils/common";

import type { AppointmentSection, CustomerSection } from "modules/Appointments/components/NewAppointment";

type CustomerProps = {
  customerType: CUSTOMER_TYPES;
  hasValidationError: boolean;
  isCustomerPreselected: boolean;
};

type AppointmentCustomerSection = {
  customer_owner: CustomerSection | null;
  customer_driver: CustomerSection | null;
  customer_contract: CustomerSection | null;
};

const getCurrentCustomerType = (type: CUSTOMER_TYPES): keyof AppointmentCustomerSection => {
  if (type === CUSTOMER_TYPES.Driver) return "customer_driver";
  if (type === CUSTOMER_TYPES.Owner) return "customer_owner";
  return "customer_contract";
};

const FormHelper = createFormHelper<AppointmentSection>();

export const CustomerForm = ({ hasValidationError, isCustomerPreselected, customerType }: CustomerProps) => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const keyloopLocation = isKeyLoopLocation(selectedLocation);

  const { watch } = FormHelper.useFormContext();
  const [carRegNr, customer_driver, customer_owner, customer_contract] = watch(["car.reg_nr", "customer_driver", "customer_contract", "customer_owner"]);

  const getCurrentCustomerTypeValues = () => {
    if (customerType === CUSTOMER_TYPES.Driver) return customer_driver;
    if (customerType === CUSTOMER_TYPES.Owner) return customer_owner;
    return customer_contract;
  };

  const customer = getCurrentCustomerTypeValues();
  const currentCustomerType = getCurrentCustomerType(customerType);

  const isCarSelected = Boolean(carRegNr);
  const isCustomerDriver = customerType === CUSTOMER_TYPES.Driver;
  const customerHasValues = hasAnyFieldValue(customer, isCustomerDriver ? CUSTOMER_DRIVER_FIELDS : CUSTOMER_OWNER_CONTRACTOR_FIELDS);
  const showLastNameError = hasValidationError && customerHasValues && !customer?.surname && isCustomerDriver ? !customer?.company : false;

  return (
    <>
      <Form.Group>
        <Form.Field width={8}>
          <label>{`${t("v8_dms", "DMS")} #`}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.dms_nr`}
            inputProps={{ placeholder: `${t("v8_dms", "DMS")} #`, fluid: true, disabled: true }}
          />
        </Form.Field>
        {!isCustomerDriver && (
          <Form.Field width={8}>
            <label>{t("v8_company", "Company")}</label>
            <FormHelper.Field.Input
              key={currentCustomerType}
              name={`${currentCustomerType}.company`}
              inputProps={{ placeholder: t("v8_company", "Company"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
            />
          </Form.Field>
        )}
      </Form.Group>
      <Form.Group>
        <Form.Field width={8}>
          <label>{t("v8_first_name", "First name")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.firstname`}
            inputProps={{ placeholder: t("v8_first_name", "First name"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
        <Form.Field width={8} error={showLastNameError}>
          <label>{t("v8_last_name", "Last name")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.surname`}
            inputProps={{ placeholder: t("v8_last_name", "Last name"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={6}>
          <label>{t("v8_street_house", "Street and house #")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.street`}
            inputProps={{ placeholder: t("v8_street_house", "Street and house #"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
        <Form.Field width={6}>
          <label>{t("v8_postcode", "Postcode")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.postcode`}
            inputProps={{ placeholder: t("v8_postcode", "Postcode"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
        <Form.Field width={6}>
          <label>{t("v8_location", "Location")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.place`}
            inputProps={{ placeholder: t("v8_location", "Location"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group>
        <Form.Field width={6}>
          <label>{t("v8_email", "Email")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.email_business`}
            inputProps={{ placeholder: t("v8_email", "Email"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
        <Form.Field width={6}>
          <label>{t("v8_mobile", "Mobile")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.tel_mobile_business`}
            inputProps={{ placeholder: t("v8_mobile", "Mobile"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
        <Form.Field width={6}>
          <label>{t("v8_phone", "Phone")}</label>
          <FormHelper.Field.Input
            key={currentCustomerType}
            name={`${currentCustomerType}.tel_business_nr`}
            inputProps={{ placeholder: t("v8_phone", "Phone"), fluid: true, disabled: !isCarSelected || isCustomerPreselected || keyloopLocation }}
          />
        </Form.Field>
      </Form.Group>
    </>
  );
};
